import { supabaseClient } from './supabase';

export interface LocationInfo {
  name: string;
  county: string;
  type: 'city' | 'county' | 'state';
}

export interface PopulationDataRow {
  location_name: string;
  county: string;
  population: number;
  year: number;
}

export interface LatestPopulationData {
  population: number;
  year: number;
}

class SupabaseLocationService {
  private locations: LocationInfo[] = [];
  private cityCountyMap: Map<string, string> = new Map();
  private initialized = false;

  async initialize() {
    if (this.initialized) {
      return; // Already initialized
    }
    
    try {
      
      // Query unique locations from the normalized population table
      // Get distinct location_name, county, and geography_type combinations
      // We'll get one row per location by filtering for a specific year (2023)
      const { data: locationData, error } = await supabaseClient
        .from('population')
        .select('location_name, county, geography_type')
        .eq('year', 2023) // Get only 2023 data to avoid duplicates
        .not('location_name', 'is', null)
        .not('geography_type', 'is', null);

      if (error) {
        console.error('Error fetching location data from Supabase:', error);
        throw error;
      }

      
      // Debug: Show raw data structure
      
      // Debug: Check for Los Angeles in raw data
      const losAngelesRaw = locationData?.find(row => 
        row.location_name?.toLowerCase().includes('los angeles')
      );

      if (locationData && locationData.length > 0) {
        this.buildLocationMaps(locationData);
      } else {
        console.warn('⚠️ No location data received from Supabase');
        throw new Error('No location data available');
      }

      this.initialized = true;
    } catch (error) {
      console.error('Failed to initialize Supabase location service:', error);
      throw error;
    }
  }



  private buildLocationMaps(locationData: any[]) {
    // Build unique locations list
    const uniqueLocations = new Map<string, LocationInfo>();
    
    locationData.forEach(row => {
      const locationName = row.location_name?.trim();
      const county = row.county?.trim();
      const geographyType = row.geography_type?.trim();
      
      if (locationName && geographyType && !uniqueLocations.has(locationName)) {
        // Map geography_type to our type system
        let type: 'city' | 'county' | 'state';
        
        if (geographyType === 'State') {
          type = 'state';
        } else if (geographyType === 'County') {
          type = 'county';
        } else if (geographyType === 'City') {
          type = 'city';
        } else {
          // Fallback to name-based detection for backward compatibility
          if (locationName === 'California') {
            type = 'state';
          } else if (locationName.endsWith(' County')) {
            type = 'county';
          } else {
            type = 'city';
          }
        }
        
        uniqueLocations.set(locationName, {
          name: locationName,
          county: county || '',
          type
        });
      }
    });

    this.locations = Array.from(uniqueLocations.values());

    // Build city to county mapping
    locationData.forEach(row => {
      const locationName = row.location_name?.trim();
      const county = row.county?.trim();
      const geographyType = row.geography_type?.trim();
      
      if (locationName && county && geographyType === 'City') {
        this.cityCountyMap.set(locationName, county);
      }
    });
  }

  getAvailableLocations(): LocationInfo[] {
    if (!this.initialized) {
      console.warn('⚠️ Location service not initialized. Call initialize() first.');
      return [];
    }
    return this.locations;
  }

  getCities(): LocationInfo[] {
    if (!this.initialized) {
      console.warn('⚠️ Location service not initialized. Call initialize() first.');
      return [];
    }
    return this.locations.filter(loc => loc.type === 'city');
  }

  getCounties(): LocationInfo[] {
    if (!this.initialized) {
      console.warn('⚠️ Location service not initialized. Call initialize() first.');
      return [];
    }
    return this.locations.filter(loc => loc.type === 'county');
  }

  getCountyForCity(cityName: string): string | null {
    if (!this.initialized) {
      console.warn('⚠️ Location service not initialized. Call initialize() first.');
      return null;
    }
    return this.cityCountyMap.get(cityName) || null;
  }

  searchLocations(query: string): LocationInfo[] {
    if (!this.initialized) {
      console.warn('⚠️ Location service not initialized. Call initialize() first.');
      return [];
    }
    
    const lowerQuery = query.toLowerCase();
    const results = this.locations.filter(loc => 
      loc.name.toLowerCase().includes(lowerQuery) ||
      loc.county.toLowerCase().includes(lowerQuery)
    );
    
    
    // Debug: If searching for "los", show more details
    if (query.toLowerCase().includes('los')) {
      const losLocations = this.locations.filter(loc => 
        loc.name.toLowerCase().includes('los') || loc.county.toLowerCase().includes('los')
      );
      
      // Debug: Check specifically for "Los Angeles"
      const losAngelesExact = this.locations.find(loc => loc.name === 'Los Angeles');
      
      // Debug: Check for partial matches
      const losAngelesPartial = this.locations.filter(loc => 
        loc.name.toLowerCase().includes('los angeles')
      );
    }
    
    return results;
  }

  // Get population data with proper Supabase filtering using normalized table
  async getPopulationData(locationName: string, year?: number): Promise<PopulationDataRow[]> {
    try {
      let query = supabaseClient
        .from('population')
        .select('location_name, county, year, population')
        .eq('location_name', locationName);

      if (year) {
        query = query.eq('year', year);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching population data from Supabase:', error);
        throw error;
      }


      if (!data || data.length === 0) {
        return [];
      }

      return data.map(row => ({
        location_name: row.location_name,
        county: row.county || '',
        population: row.population,
        year: row.year
      }));
    } catch (error) {
      console.error('Failed to fetch population data from Supabase:', error);
      throw error;
    }
  }

  // Get latest population with proper Supabase filtering using normalized table
  async getLatestPopulation(locationName: string): Promise<LatestPopulationData | null> {
    try {
      const { data, error } = await supabaseClient
        .from('population')
        .select('population, year')
        .eq('location_name', locationName)
        .not('population', 'is', null)
        .order('year', { ascending: false })
        .limit(1);

      if (error) {
        console.error('Error fetching latest population from Supabase:', error);
        throw error;
      }


      if (!data || data.length === 0) {
        return null;
      }

      return data[0];
    } catch (error) {
      console.error('Failed to fetch latest population from Supabase:', error);
      throw error;
    }
  }

  // Get geofips for a location
  async getGeofips(locationName: string): Promise<number | null> {
    try {
      
      const { data, error } = await supabaseClient
        .from('population')
        .select('geofips')
        .eq('location_name', locationName)
        .limit(1);

      if (error) {
        console.error('Error fetching geofips from Supabase:', error);
        throw error;
      }


      if (data && data.length > 0) {
        const geofips = data[0].geofips;
        return geofips;
      }

      return null;
    } catch (error) {
      console.error('Failed to fetch geofips from Supabase:', error);
      throw error;
    }
  }

  // Safeguard method to ensure Supabase is being used
  isUsingSupabase(): boolean {
    return this.initialized;
  }

  // Method to validate that no CSV loading is happening
  validateNoCSVLoading(): void {
    if (!this.initialized) {
      throw new Error('❌ Location service must be initialized with Supabase before use');
    }
    
    // Additional validation that we're not accidentally loading files
    const stack = new Error().stack;
    if (stack && stack.includes('fetch') && stack.includes('.csv')) {
      throw new Error('❌ CSV loading detected! All data must come from Supabase with proper filtering.');
    }
  }
}

export const supabaseLocationService = new SupabaseLocationService();
